import { ChangeDetectorRef, Component, effect, ElementRef, inject, input, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { CheckoutPaymentTypeFacade } from '@spartacus/checkout/b2b/root';
import { CheckoutStepService } from '@spartacus/checkout/base/components';
import { CheckoutStepType } from '@spartacus/checkout/base/root';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { DpBaseSiteService } from '@app/core/services/base-site.service';
import { DpGtmService } from '@app/core/services/gtm.service';
import { DpCartService } from '@app-modules/cart/shared';
import { END_USER_PURCHASE_NUMBER } from '@app-modules/checkout/modules/payment-type/constants';
import { DpCheckoutService } from '@app-modules/checkout/services';

@Component({
    selector: 'dp-purchase-order-number',
    templateUrl: './purchase-order-number.component.html',
    styleUrls: ['./purchase-order-number.component.scss'],
    standalone: false
})
export class DpPurchaseOrderNumberComponent {
  @ViewChild('poNumberRef') poNumberRef?: ElementRef;
  isReadonly = input<boolean>(false);
  form: UntypedFormGroup;
  loading$: Observable<boolean>;
  hasUserPurchaseNumber$: Observable<boolean | undefined>;
  requiredPurchaseOrderNumber$: Observable<boolean | undefined>;
  purchaseOrderNumberPlaceholder$: Observable<string>;
  deliveryModeRoute = inject(CheckoutStepService).getCheckoutStepRoute(CheckoutStepType.DELIVERY_MODE) || '';
  readonly #changeDetectorRef = inject(ChangeDetectorRef);

  constructor(
    private readonly checkoutPaymentTypeFacade: CheckoutPaymentTypeFacade,
    private readonly dpGtmService: DpGtmService,
    private readonly fb: UntypedFormBuilder,
    private readonly dpCheckoutService: DpCheckoutService,
    private readonly cartService: DpCartService,
    readonly baseService: DpBaseSiteService
  ) {
    this.form = this.fb.group({
      poNumber: ['', [Validators.maxLength(20)]],
      orderOpenText: ['', [Validators.maxLength(35)]]
    });
    effect(() => (this.isReadonly() ? this.form.disable() : this.form.enable()));
    this.hasUserPurchaseNumber$ = baseService
      .getDefault()
      .pipe(map((baseSite) => baseSite?.baseStore?.orderEntryAdditionalFields?.includes(END_USER_PURCHASE_NUMBER)));

    this.requiredPurchaseOrderNumber$ = baseService
      .getDefault()
      .pipe(map((baseSite) => baseSite?.baseStore?.requiredPurchaseOrderNumber));

    this.requiredPurchaseOrderNumber$.pipe(takeUntilDestroyed()).subscribe((requiredPurchaseOrderNumber) => {
      if (requiredPurchaseOrderNumber) {
        this.form.get('poNumber')?.addValidators(Validators.required);
      } else {
        this.form.get('poNumber')?.removeValidators(Validators.required);
      }
      this.dpCheckoutService.setSelectedPONumberRequired(requiredPurchaseOrderNumber);
    });

    this.purchaseOrderNumberPlaceholder$ = this.requiredPurchaseOrderNumber$.pipe(
      map((value) => (value ? 'checkoutPO.placeholderRequired' : 'checkoutPO.placeholder'))
    );

    this.loading$ = this.dpCheckoutService.processing$.asObservable();

    this.checkoutPaymentTypeFacade
      .getPurchaseOrderNumberState()
      .pipe(take(1))
      .subscribe((cartPoNumber) => {
        if (cartPoNumber.data) {
          this.form.get('poNumber')?.setValue(cartPoNumber.data);
        }
      });

    this.cartService
      .getActive()
      .pipe(take(1))
      .subscribe((cart) => {
        this.dpCheckoutService.setPONumber(cart.purchaseOrderNumber);
        this.dpCheckoutService.setOrderOpenText(cart.openText);
        this.form.get('orderOpenText')?.setValue(cart.openText);
      });
    this.dpGtmService.pushCheckoutTagV2('begin_checkout');
    this.dpCheckoutService.invalidPONumber$.pipe(takeUntilDestroyed(), filter(Boolean)).subscribe(() => {
      (this.poNumberRef?.nativeElement as HTMLInputElement)?.focus();
      this.form.markAsDirty();
      this.form.markAllAsTouched();
      this.#changeDetectorRef.markForCheck();
    });
    this.form
      .get('poNumber')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((value) => this.dpCheckoutService.setPONumber(value));
    this.form
      .get('orderOpenText')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((value) => this.dpCheckoutService.setOrderOpenText(value));
  }
}
